/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import styled, { css } from "styled-components";

import { lightTheme } from "src/app/themes";
import { StandardInput } from "src/components/UI/Form";

export const FieldWrapper = styled.div`
  padding: 5px;
`;

/* -------------------------------------------------- */
/*                       Inputs                       */
/* -------------------------------------------------- */

export const SignupInput = styled(StandardInput)<{ $validity: string | null }>`
  box-shadow: ${({ $validity }) =>
    $validity === "INVALID"
      ? "inset 0 0 0px 3px #904d4d"
      : $validity === "VALID"
      ? "inset 0 0 0px 3px #4D9090"
      : "none"};
`;

export const UsernameInput = styled(StandardInput)<{ $taken?: string }>`
  box-shadow: ${({ $taken }) =>
    $taken === "TAKEN" || $taken === "INVALID" || $taken === "LENGTH"
      ? "inset 0 0 0px 3px #904d4d"
      : $taken === "AVAILABLE"
      ? "inset 0 0 0px 3px #4D9090"
      : ""} !important;
`;

/* -------------------------------------------------- */
/*                     From Comps                     */
/* -------------------------------------------------- */

export const LabelWrap = styled.div`
  font-family: "Inter", -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif,
    "Apple Color Emoji";
  -webkit-font-smoothing: antialiased;
  font-size: 16px;
  font-weight: 400;
  color: #494949;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 2;
  min-height: 2rem;
`;

export const FormValidationErrorMessage = styled.div`
  padding-top: 5px;
  padding-bottom: 5px;
  color: #aeaeae;
  height: 15px;
`;

export const SkipButton = styled.div<{ $notLink?: boolean }>`
  color: #aeaeae;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  margin-top: 12px;
  text-align: center;
  ${($notLink) =>
    !$notLink &&
    css`
      :hover {
        text-decoration: underline;
      }
    `}
`;

/* -------------------------------------------------- */
/*                     Typogrpahy                     */
/* -------------------------------------------------- */

export const SignUpParagraph = styled.p`
  font-family: "Inter", -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 15px;
  line-height: 20px;
  margin-bottom: 15px;
  color: ${({ theme }) => theme.text.default};
`;

export const BlockSignUpParagraph = styled(SignUpParagraph)`
  text-align: center;
  padding-bottom: 35px;
`;

export const SignUpParagraphDoubleSpaced = styled(SignUpParagraph)`
  line-height: 24px;
  margin-left: 5px;
`;

export const EndOfFormText = styled.div<{ alwaysLightMode?: boolean }>`
  padding: 5px;
  padding-top: 12px;
  color: ${({ theme, alwaysLightMode }) => (alwaysLightMode ? lightTheme.text.default : theme.text.default)};
`;

export const SoftLogout = styled.div`
  color: #808080;
  cursor: pointer;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  text-align: right;
  font-weight: 400;
  margin-right: 27px;
  margin-top: 23px;
  position: absolute;
  top: 0;
  right: 0;
  :hover {
    text-decoration: underline;
  }
`;

export const StyledText = styled.span`
  color: ${({ theme }) => theme.text.default};
`;

export const DraggableHeader = styled.div`
  position: absolute;
  top: 0;
  height: 50px;
  width: 90%;
  -webkit-app-region: drag;
`;

export const InlineDataSpan = styled.code`
  font-family: "IBM Plex Mono", monospace;
  display: inline-block;
  padding: 0px 3px;
  background-color: rgba(175, 184, 193, 0.371);
`;

export const AuthHeader = styled.div`
  line-height: 1.15;
  -webkit-text-size-adjust: 100%;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif,
    "Apple Color Emoji";
  color: #000000;
  -webkit-font-smoothing: antialiased;
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
  margin-top: 64px;
`;

/* -------------------------------------------------- */
/*                       Layout                       */
/* -------------------------------------------------- */

interface AuthWrapperProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

export const AuthWrapper = ({ children, style }: AuthWrapperProps) => (
  <AuthWrapperStyles style={style}>
    <DraggableHeader onDoubleClick={() => (window as any).electronAPI?.send("doubleClick")} />
    {children}
  </AuthWrapperStyles>
);

const AuthWrapperStyles = styled.div`
  display: flex;
`;

export const AuthBody = styled.div`
  padding-top: 32px;
  width: 90%;
  max-width: 500px;
  font-family: "Inter";
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 100px;
`;
